import { useQuery } from '@tanstack/react-query';
import {
  CalendarBlank,
  Gavel,
  Users,
  CircleNotch as Loader2,
} from '@phosphor-icons/react';
import { fetchMyCalendar, type PortalHearing, type PortalConference } from '@/lib/solicitorPortalApi';

/**
 * Calendar view — role-filtered.
 *   - Hearings: read-only info cards (your counsel is in court, you are not)
 *   - Conferences: interactive (you're an attendee)
 * Internal chambers events are filtered out at the API layer.
 */
export default function CalendarPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['portal', 'calendar'],
    queryFn: () => fetchMyCalendar(),
  });

  type Item =
    | { kind: 'hearing'; at: Date; row: PortalHearing }
    | { kind: 'conference'; at: Date; row: PortalConference };

  const items: Item[] = [
    ...(data?.hearings.map<Item>((h) => ({
      kind: 'hearing',
      at: new Date(h.hearingDate),
      row: h,
    })) ?? []),
    ...(data?.conferences.map<Item>((c) => ({
      kind: 'conference',
      at: new Date(c.startAt),
      row: c,
    })) ?? []),
  ].sort((a, b) => a.at.getTime() - b.at.getTime());

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Calendar</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Conferences you're attending, and hearings on your matters. Internal chambers events are hidden.
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading calendar…
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-5 text-sm text-red-800 dark:text-red-300">
          {error instanceof Error ? error.message : 'Could not load calendar.'}
        </div>
      )}

      {data && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-card p-10 text-center">
          <CalendarBlank className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <div className="mb-1 text-sm font-medium text-foreground">Nothing scheduled</div>
          <p className="text-xs text-muted-foreground">
            Hearings and conferences on your matters will appear here in the next 60 days.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <ul className="space-y-3">
          {items.map((item) =>
            item.kind === 'hearing' ? (
              <HearingCard key={`h-${item.row.id}`} h={item.row} />
            ) : (
              <ConferenceCard key={`c-${item.row.id}`} c={item.row} />
            ),
          )}
        </ul>
      )}

      <div className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-xs text-muted-foreground">
        <b>Note:</b> hearings are shown as information only — you aren't the advocate. When a
        conference is proposed, you'll be able to confirm the slot from this page.
      </div>
    </div>
  );
}

function HearingCard({ h }: { h: PortalHearing }) {
  const d = new Date(h.hearingDate);
  return (
    <li className="flex items-start gap-4 rounded-lg border border-border bg-muted/20 p-4">
      <DateBlock d={d} muted />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Gavel className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Court hearing · Your counsel is attending
          </span>
        </div>
        <div className="mt-1 text-sm font-medium text-foreground">
          {h.case?.caseName ?? 'Hearing'} — {h.hearingType.replace(/_/g, ' ')}
        </div>
        <div className="text-xs text-muted-foreground">
          {h.court?.name ?? '—'}
          {h.courtroom && ` · Court ${h.courtroom}`}
          {' · '}
          {h.durationMinutes} min
        </div>
      </div>
    </li>
  );
}

function ConferenceCard({ c }: { c: PortalConference }) {
  const start = new Date(c.startAt);
  const end = new Date(c.endAt);
  return (
    <li className="flex items-start gap-4 rounded-lg border border-brand-200 bg-brand-50/40 p-4">
      <DateBlock d={start} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Users className="h-3.5 w-3.5 text-brand-600 dark:text-brand-400" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-700 dark:text-brand-300">
            Conference · You're attending
          </span>
        </div>
        <div className="mt-1 text-sm font-medium text-foreground">{c.title}</div>
        <div className="text-xs text-muted-foreground">
          {start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} –{' '}
          {end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          {c.location && ` · ${c.location}`}
          {c.case && ` · ${c.case.caseName}`}
        </div>
      </div>
    </li>
  );
}

function DateBlock({ d, muted = false }: { d: Date; muted?: boolean }) {
  return (
    <div
      className={`flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-md border text-center ${
        muted ? 'border-border bg-card text-muted-foreground' : 'border-brand-300 bg-card text-brand-700 dark:text-brand-300'
      }`}
    >
      <div className="text-[9px] font-bold uppercase tracking-wider">
        {d.toLocaleDateString('en-GB', { month: 'short' })}
      </div>
      <div className="text-lg font-semibold leading-none">{d.getDate()}</div>
      <div className="mt-0.5 text-[9px] uppercase">
        {d.toLocaleDateString('en-GB', { weekday: 'short' })}
      </div>
    </div>
  );
}
